"use client"

import {useState, useCallback} from "react"
import LoanRateComparatorForm from "@/app/components/loanRateComparator/LoanRateComparatorForm"
import LoanRateComparatorTable from "@/app/components/loanRateComparator/LoanRateComparatorTable"
import LoanRateComparatorFeaturedRates from "@/app/components/loanRateComparator/LoanRateComparatorFeaturedRates"
import {LoanRateData, getLoanRates} from "@/app/actions/getLoanRates"
import {
    DEFAULT_LOAN_AMOUNT,
    DEFAULT_LOAN_YEAR_DURATION,
    DEFAULT_BORROWER_AGE,
} from "@/app/(pages)/taux-pret-immobilier/utils"

interface LoanRateComparatorProps {
    initialData: LoanRateData
    hideFeaturedRates?: boolean
}

export default function LoanRateComparator({initialData, hideFeaturedRates = false}: LoanRateComparatorProps) {
    const [isLoading, setIsLoading] = useState(false)
    const [loanRateData, setLoanRateData] = useState<LoanRateData>(initialData)
    const [selectedRegion, setSelectedRegion] = useState("")

    const fetchLoanRates = useCallback(async (
        loanAmount: number = DEFAULT_LOAN_AMOUNT,
        loanDuration: number = DEFAULT_LOAN_YEAR_DURATION,
        borrowerAge: number = DEFAULT_BORROWER_AGE,
        region: string = "",
    ) => {
        setIsLoading(true)
        try {
            const result = await getLoanRates(loanAmount, loanDuration, borrowerAge, region)
            setLoanRateData(result)
        } catch (error) {
            console.error("Error fetching loan rates:", error)
            setLoanRateData({
                banksRanked: [],
                featuredRates: [
                    {duration: 15, interestRate: "N/A", evolutionClass: "fa-circle-exclamation text-yellow-500"},
                    {duration: 20, interestRate: "N/A", evolutionClass: "fa-circle-exclamation text-yellow-500"},
                    {duration: 25, interestRate: "N/A", evolutionClass: "fa-circle-exclamation text-yellow-500"},
                ],
            })
        } finally {
            setIsLoading(false)
        }
    }, [])

    return (
        <>
            {!hideFeaturedRates &&
                <LoanRateComparatorFeaturedRates
                    featuredRates={loanRateData.featuredRates}
                    isLoading={isLoading}
                    isVariant={false}
                />
            }

            <div className="flex flex-col lg:flex-row gap-6 relative">
                <div
                    className="h-fit bg-white rounded-2xl shadow-xl lg:w-1/3 lg:sticky lg:top-28"
                    data-test="loan-rate-form"
                >
                    <LoanRateComparatorForm
                        onInputChange={fetchLoanRates}
                        selectedRegion={selectedRegion}
                        onRegionChange={setSelectedRegion}
                    />
                </div>

                <div className="flex flex-col h-full w-full lg:w-2/3" data-test="loan-rate-table">
                    <LoanRateComparatorTable
                        banksRanked={loanRateData.banksRanked}
                        isLoading={isLoading}
                        isVariant={false}
                    />
                </div>
            </div>
        </>
    )
}
